import { secureStorage } from "@/utils/aes";
import { type ApiResponse, refreshApi } from "@/utils/api";
import { Buffer } from "buffer";
import { create } from "zustand";
import { persist, type StorageValue } from "zustand/middleware";
import { useCryptStore } from "./crypt";

interface TokenState {
  isAuthenticated: boolean;
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
  refreshTokenExpiresAt: number;
  refresh: () => Promise<string>;
  logout: () => Promise<void>;
  setAccessToken: (token: string) => void;
  setRefreshToken: (refreshToken: string) => void;
}
const getExpiresAtFromToken = (token: string, defaultValue: number): number => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000; // convert to Current TimeZone timestamp
  } catch {
    return defaultValue; // default to provided value if parsing fails
  }
};
export const useTokenStore = create(
  persist<TokenState>(
    (set, get) => ({
      isAuthenticated: false,
      accessToken: "",
      refreshToken: "",
      accessTokenExpiresAt: 0,
      refreshTokenExpiresAt: 0,
      refresh: async () => {
        console.debug("Refreshing access token...");
        const { data } =
          await refreshApi.post<ApiResponse<string>>("/auth/user/refresh");
        get().setAccessToken(data.data!);
        console.debug("Access token refreshed successfully");
        return data.data!;
      },
      logout: async () => {
        try {
          const state = useCryptStore.getState();
          const encrypted_data = await state.aes_gcm_encrypt(
            Buffer.from(JSON.stringify({ token: get().accessToken })).buffer,
          );
          await refreshApi.post<ApiResponse<null>>(
            "/auth/user/logout",
            encrypted_data,
          );
        } catch (e) {
          console.log(e);
        }
        set({
          isAuthenticated: false,
          accessToken: "",
          refreshToken: "",
          accessTokenExpiresAt: 0,
          refreshTokenExpiresAt: 0,
        });
      },
      setAccessToken: (accessToken: string) => {
        const accessTokenExpiresAt = getExpiresAtFromToken(
          accessToken,
          Date.now() + 59 * 60 * 1000,
        ); // 59 minutes
        console.log(
          "Setting access token, expires at:",
          new Date(accessTokenExpiresAt),
        );
        set({ accessToken, accessTokenExpiresAt, isAuthenticated: true });
      },
      setRefreshToken: (refreshToken: string) => {
        const refreshTokenExpiresAt = getExpiresAtFromToken(
          refreshToken,
          Date.now() + 7 * 24 * 60 * 60 * 1000 - 5 * 60 * 1000,
        ); // 7 days minus 5 minutes
        console.log(
          "Setting refresh token, expires at:",
          new Date(refreshTokenExpiresAt),
        );
        set({ refreshToken, refreshTokenExpiresAt });
      },
    }),
    {
      name: "encrypted-token-storage",
      storage: {
        getItem: async (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const value: StorageValue<TokenState> = JSON.parse(
            (await secureStorage.decrypt(str)) || "",
          );
          if (!value || typeof value === "string") {
            localStorage.removeItem(name);
            return null;
          }
          if (value.state.refreshTokenExpiresAt < Date.now()) {
            localStorage.removeItem(name);
            return null;
          }
          if (value.state.accessTokenExpiresAt < Date.now()) {
            try {
              console.log("Access token expired, attempting to refresh...");
              const { data } =
                await refreshApi.post<ApiResponse<string>>(
                  "/auth/user/refresh",
                );
              value.state.accessToken = data.data!;
              value.state.accessTokenExpiresAt = getExpiresAtFromToken(
                data.data!,
                Date.now() + 59 * 60 * 1000,
              );
              localStorage.setItem(
                name,
                await secureStorage.encrypt(JSON.stringify(value)),
              );
              return value;
            } catch (error) {
              console.error("Token refresh failed:", error);
              localStorage.removeItem(name);
              return null;
            }
          }
          return value;
        },
        setItem: async (name, value) => {
          const encrypted = await secureStorage.encrypt(JSON.stringify(value));
          localStorage.setItem(name, encrypted);
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    },
  ),
);
